const API_BASE = process.env.REACT_APP_API_URL || '';

async function request(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const config = {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  };

  const response = await fetch(url, config);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || `Request failed: ${response.status}`);
  }

  return data;
}

export function fetchCalls(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/calls${query ? `?${query}` : ''}`);
}

export function fetchCallById(callId) {
  return request(`/api/calls/${encodeURIComponent(callId)}`);
}

export function fetchCallbacks(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/callbacks${query ? `?${query}` : ''}`);
}

export function triggerCallback(callId, phoneNumber) {
  return request('/api/callbacks/trigger', {
    method: 'POST',
    body: JSON.stringify({ call_id: callId, phone_number: phoneNumber }),
  });
}

export function fetchAnalytics() {
  return request('/api/analytics/summary');
}

export function fetchHealth() {
  return request('/api/health');
}

export function fetchSubmissions(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/submissions${query ? `?${query}` : ''}`);
}

export function deleteCall(callId) {
  return request(`/api/calls/${encodeURIComponent(callId)}`, { method: 'DELETE' });
}

export function bulkDeleteCalls(callIds) {
  return request('/api/calls/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ call_ids: callIds }),
  });
}

export function deleteCallback(callbackId) {
  return request(`/api/callbacks/${encodeURIComponent(callbackId)}`, { method: 'DELETE' });
}

export function bulkDeleteCallbacks(callbackIds) {
  return request('/api/callbacks/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ callback_ids: callbackIds }),
  });
}

export function deleteSubmission(submissionId) {
  return request(`/api/submissions/${encodeURIComponent(submissionId)}`, { method: 'DELETE' });
}

export function fetchFeedback(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/feedback${query ? `?${query}` : ''}`);
}

export function deleteFeedback(feedbackId) {
  return request(`/api/feedback/${encodeURIComponent(feedbackId)}`, { method: 'DELETE' });
}

export function fetchFeedbackAnalytics() {
  return request('/api/feedback/analytics');
}

export async function exportFeedbackCsv() {
  const url = `${API_BASE}/api/feedback/export`;
  const res = await fetch(url);
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'feedback_export.csv';
  link.click();
  URL.revokeObjectURL(link.href);
}

export function fetchBankSurveys(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/bank-surveys${query ? `?${query}` : ''}`);
}

export function deleteBankSurvey(surveyId) {
  return request(`/api/bank-surveys/${encodeURIComponent(surveyId)}`, { method: 'DELETE' });
}

export function fetchBankSurveyAnalytics() {
  return request('/api/bank-surveys/analytics');
}

export function fetchImpactSurveys(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/impact-surveys${query ? `?${query}` : ''}`);
}

export function deleteImpactSurvey(surveyId) {
  return request(`/api/impact-surveys/${encodeURIComponent(surveyId)}`, { method: 'DELETE' });
}

export function fetchImpactSurveyAnalytics(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/impact-surveys/analytics${query ? `?${query}` : ''}`);
}

export async function exportImpactSurveyCsv(params = {}) {
  const filtered = Object.entries(params).filter(
    ([, v]) => v != null && v !== '' && v !== 'all'
  );
  const query = new URLSearchParams(filtered).toString();
  const url = `${API_BASE}/api/impact-surveys/export${query ? `?${query}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'impact_survey_export.csv';
  link.click();
  URL.revokeObjectURL(link.href);
}

export function fetchSermonFeedback(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/sermon-feedback${query ? `?${query}` : ''}`);
}

export function deleteSermonFeedbackEntry(feedbackId) {
  return request(`/api/sermon-feedback/${encodeURIComponent(feedbackId)}`, { method: 'DELETE' });
}

export function fetchSermonFeedbackAnalytics() {
  return request('/api/sermon-feedback/analytics');
}

export async function exportSermonFeedbackCsv(params = {}) {
  const filtered = Object.entries(params).filter(
    ([, v]) => v != null && v !== '' && v !== 'all'
  );
  const query = new URLSearchParams(filtered).toString();
  const url = `${API_BASE}/api/sermon-feedback/export${query ? `?${query}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'sermon_feedback_export.csv';
  link.click();
  URL.revokeObjectURL(link.href);
}

export function fetchCampaignSurveys(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/campaign-surveys${query ? `?${query}` : ''}`);
}

export function deleteCampaignSurvey(surveyId) {
  return request(`/api/campaign-surveys/${encodeURIComponent(surveyId)}`, { method: 'DELETE' });
}

export function fetchCampaignSurveyAnalytics() {
  return request('/api/campaign-surveys/analytics');
}

export async function exportCampaignSurveyCsv(params = {}) {
  const filtered = Object.entries(params).filter(
    ([, v]) => v != null && v !== '' && v !== 'all'
  );
  const query = new URLSearchParams(filtered).toString();
  const url = `${API_BASE}/api/campaign-surveys/export${query ? `?${query}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'campaign_survey_export.csv';
  link.click();
  URL.revokeObjectURL(link.href);
}

export function fetchHackathonSurveys(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/hackathon-surveys${query ? `?${query}` : ''}`);
}

export function deleteHackathonSurvey(surveyId) {
  return request(`/api/hackathon-surveys/${encodeURIComponent(surveyId)}`, { method: 'DELETE' });
}

export function fetchHackathonSurveyAnalytics() {
  return request('/api/hackathon-surveys/analytics');
}

export async function exportHackathonSurveyCsv(params = {}) {
  const filtered = Object.entries(params).filter(
    ([, v]) => v != null && v !== '' && v !== 'all'
  );
  const query = new URLSearchParams(filtered).toString();
  const url = `${API_BASE}/api/hackathon-surveys/export${query ? `?${query}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'hackathon_survey_export.csv';
  link.click();
  URL.revokeObjectURL(link.href);
}

export function fetchYoungProfRsvps(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/young-prof-rsvps${query ? `?${query}` : ''}`);
}

export function deleteYoungProfRsvp(rsvpId) {
  return request(`/api/young-prof-rsvps/${encodeURIComponent(rsvpId)}`, { method: 'DELETE' });
}

export function fetchYoungProfRsvpAnalytics() {
  return request('/api/young-prof-rsvps/analytics');
}

export async function exportYoungProfRsvpCsv(params = {}) {
  const filtered = Object.entries(params).filter(
    ([, v]) => v != null && v !== '' && v !== 'all'
  );
  const query = new URLSearchParams(filtered).toString();
  const url = `${API_BASE}/api/young-prof-rsvps/export${query ? `?${query}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'young_prof_rsvp_export.csv';
  link.click();
  URL.revokeObjectURL(link.href);
}

export function fetchTakeoffRsvps(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/takeoff-rsvps${query ? `?${query}` : ''}`);
}

export function deleteTakeoffRsvp(rsvpId) {
  return request(`/api/takeoff-rsvps/${encodeURIComponent(rsvpId)}`, { method: 'DELETE' });
}

export function fetchTakeoffRsvpAnalytics() {
  return request('/api/takeoff-rsvps/analytics');
}

export async function exportTakeoffRsvpCsv(params = {}) {
  const filtered = Object.entries(params).filter(
    ([, v]) => v != null && v !== '' && v !== 'all'
  );
  const query = new URLSearchParams(filtered).toString();
  const url = `${API_BASE}/api/takeoff-rsvps/export${query ? `?${query}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'takeoff_rsvp_export.csv';
  link.click();
  URL.revokeObjectURL(link.href);
}

export function fetchNitaSurveys(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/nita-surveys${query ? `?${query}` : ''}`);
}

export function deleteNitaSurvey(surveyId) {
  return request(`/api/nita-surveys/${encodeURIComponent(surveyId)}`, { method: 'DELETE' });
}

export function fetchNitaSurveyAnalytics() {
  return request('/api/nita-surveys/analytics');
}

export async function exportNitaSurveyCsv(params = {}) {
  const filtered = Object.entries(params).filter(
    ([, v]) => v != null && v !== '' && v !== 'all'
  );
  const query = new URLSearchParams(filtered).toString();
  const url = `${API_BASE}/api/nita-surveys/export${query ? `?${query}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'nita_survey_export.csv';
  link.click();
  URL.revokeObjectURL(link.href);
}

export function fetchAudioFiles() {
  return request('/api/audio');
}

export async function uploadAudio(file) {
  const formData = new FormData();
  formData.append('file', file);
  const url = `${API_BASE}/api/audio/upload`;
  const res = await fetch(url, { method: 'POST', body: formData });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Upload failed');
  return data;
}

export function deleteAudioFile(filename) {
  return request(`/api/audio/${encodeURIComponent(filename)}`, { method: 'DELETE' });
}
